import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { WorkOrder } from '../types';

const COMPANY_NAME = 'Parte Diario';

/**
 * Saves or Shares a generated PDF based on the platform.
 */
export const saveOrSharePDF = async (doc: any, fileName: string) => {
    try {
        const base64Data = doc.output('datauristring').split(',')[1];
        await saveOrSharePDFFromBase64(base64Data, fileName);
    } catch (error: any) {
        console.error('Error saving/sharing PDF:', error);
        alert('Error al generar o compartir el PDF. ' + (error?.message || String(error)));
    }
};

/**
 * P2: Guarda/comparte PDF desde base64 (usado tras generación en Worker)
 */
export const saveOrSharePDFFromBase64 = async (base64Data: string, fileName: string) => {
    try {
        if (Capacitor.isNativePlatform()) {
            try {
                const tempResult = await Filesystem.writeFile({
                    path: fileName,
                    data: base64Data,
                    directory: Directory.Cache,
                });
                await Share.share({
                    title: 'Informe PDF',
                    text: 'Adjunto informe generado.',
                    url: tempResult.uri,
                    dialogTitle: 'Compartir Informe',
                });
            } catch (shareError) {
                const externalResult = await Filesystem.writeFile({
                    path: fileName,
                    data: base64Data,
                    directory: Directory.External,
                });
                await Share.share({
                    title: 'Informe PDF',
                    text: 'Adjunto informe generado.',
                    url: externalResult.uri,
                    dialogTitle: 'Compartir Informe',
                });
            }
        } else {
            const blob = base64ToBlob(base64Data, 'application/pdf');
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            // Retardo seguro para que WebKit/Blink complete la lectura del Blob antes de revocarlo
            setTimeout(() => {
                URL.revokeObjectURL(url);
            }, 1000);
        }
    } catch (error: any) {
        console.error('Error saving/sharing PDF:', error);
        throw error;
    }
};

function base64ToBlob(base64: string, mimeType: string): Blob {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
}

/**
 * P2: Genera PDF en Web Worker (no bloquea UI) con guardián de tiempo de 30s
 */
async function generatePDFInWorker(
    type: 'serviceReport' | 'summaryReport',
    payload: any
): Promise<{ base64: string; fileName: string }> {
    return new Promise((resolve, reject) => {
        let worker: Worker | null = null;
        const timeoutId = setTimeout(() => {
            if (worker) {
                try { worker.terminate(); } catch {}
                worker = null;
            }
            reject(new Error('La generación del PDF excedió el tiempo límite de seguridad (30s)'));
        }, 30000);

        try {
            worker = new Worker(new URL('../workers/pdf.worker.ts', import.meta.url), { type: 'module' });
            worker.onmessage = (e) => {
                clearTimeout(timeoutId);
                if (worker) {
                    try { worker.terminate(); } catch {}
                    worker = null;
                }
                if (e.data.error) reject(new Error(e.data.error));
                else resolve(e.data);
            };
            worker.onerror = (err) => {
                clearTimeout(timeoutId);
                if (worker) {
                    try { worker.terminate(); } catch {}
                    worker = null;
                }
                reject(err);
            };
            worker.postMessage({ type, payload });
        } catch (err) {
            clearTimeout(timeoutId);
            reject(err);
        }
    });
}

export const generateServiceReport = async (
    order: WorkOrder,
    technicianName: string,
    companyName: string = COMPANY_NAME,
    companyLogo?: string,
    dateRange?: { start?: string; end?: string }
) => {
    try {
        const { base64, fileName } = await generatePDFInWorker('serviceReport', {
            order,
            technicianName,
            companyName,
            companyLogo,
            dateRange,
        });
        await saveOrSharePDFFromBase64(base64, fileName);
    } catch (err) {
        console.error('Worker PDF failed:', err);
        throw new Error('No se pudo generar el reporte de servicio en el worker.');
    }
};


export const generateSummaryReport = async (
    orders: WorkOrder[],
    clientName: string,
    companyName: string = COMPANY_NAME,
    companyLogo?: string,
    dateRange?: { start?: string; end?: string }
) => {
    try {
        const { base64, fileName } = await generatePDFInWorker('summaryReport', {
            orders,
            clientName,
            companyName,
            companyLogo,
            dateRange,
        });
        await saveOrSharePDFFromBase64(base64, fileName);
    } catch (err) {
        console.error('Worker PDF failed:', err);
        throw new Error('No se pudo generar el resumen de reportes en el worker.');
    }
};
